import { useUserContext } from "../../auth/userContextProvider/UserContextProvider";
import {
    allFrameworkColumns,
    CreateFromTemplateViewModel,
    FrameworkColumn,
    FrameworkExportHeader,
    FrameworkOverviewModel,
    FrameworkRowTableModel,
    FrameworkRowViewModel,
    FrameworksSavedFilter,
} from "./Frameworks.types";
import { useTranslation } from "@localization/useTranslation";
import { getFrameworkTemplateUrl, useFetchFrameworkTemplates } from "../frameworkTemplates/FrameworkTemplates.hooks.ts";
import { useState } from "react";
import { GetFrameworkPlanLimit } from "../../auth/planProvider/PlanProvider.hooks.ts";
import { useFeatureFlags, usePlanContext } from "../../auth/planProvider/PlanProvider.tsx";
import { FeatureFlag } from "../../auth/planProvider/PlanProvider.types.ts";
import { FilterOption, FilterOptionType, FrameworkFieldGrouping } from "@common/components/filterPopover/FilterPopover.types.ts";
import { applyClientFilters, withOperators } from "@common/components/filterBar/filterOperatorMatching.ts";
import { GetListData } from "@common/components/filterPopover/subMenues/SubMenuDataHelper";
import { FilterOperatorUi } from "@common/api/filterTypes";
import { useSeedFilterFromUrl } from "@common/hooks/useSeedFilterFromUrl.ts";
import { getUniqueFilterOptions, sortByName } from "@common/hooks/filterUtils";
import { TableFields } from "@common/components/tableDisplayFields/TableFields.types";
import { usePageView } from "@common/pageView/PageView.hooks";
import { FilterPage, SessionStorageSavedView } from "@common/pageView/PageView.types";
import { useSavedViewQuery } from "@common/components/savedViews/SavedViewsMenu.hooks";
import { SavedView } from "@common/components/savedViews/SavedViewsMenu.types";
import { getDisplayedRows } from "@common/tableSearch";
import { DotLegalTableState, ITableHeader, useDotLegalSnackbar } from "@dotlegal/dotlegal-ui-components";
import { useCustomListExport } from "../../customLists/customListView/useCustomListExport";

import { useDotLegalQuery, useDotLegalQueryClient } from "@common/hooks/dotLegalReactQuery";

export const frameworkTemplateFilterKey = "frameworkTemplates";
const frameworkFilterId = "framework";
const frameworkTemplateFilterId = "frameworkTemplate";
const customFrameworkTemplateId = "custom";
const showOnlyMineFilterId = "show-only-mine";

// The row property the table sorts each column on. It differs from the column id wherever the cell renders something
// other than a plain field, so this is the single place a column's sort target is defined.
const sortPropertyByColumn: Record<FrameworkColumn, keyof FrameworkRowTableModel> = {
    [FrameworkColumn.Framework]: "framework",
    [FrameworkColumn.CertificationRequired]: "isCertificationRequired",
    [FrameworkColumn.Owner]: "ownerAsString",
    [FrameworkColumn.ComplianceAreas]: "complianceAreaAsString",
    [FrameworkColumn.GroupCompanies]: "groupCompaniesAsString",
    [FrameworkColumn.Controls]: "numberOfTotalControls",
    [FrameworkColumn.Status]: "statusAsString",
    [FrameworkColumn.Progress]: "progress",
};

// Must deep-equal the backend strategy's GetDefaultFilterData after a JSON round-trip, or the system default view
// would be flagged as changed before the user touches anything.
const defaultFrameworksFilter: FrameworksSavedFilter = {
    selectFilters: {},
    filterOperators: {},
    showOnlyMine: false,
};

function isFrameworkColumn(column: string): column is FrameworkColumn {
    return allFrameworkColumns.includes(column as FrameworkColumn);
}

export default function useFrameworksHooks() {
    const queryClient = useDotLegalQueryClient();
    const snackbar = useDotLegalSnackbar();
    const featureFlags = useFeatureFlags();
    const { addonsFeatures } = usePlanContext();
    const { translateString, translateFrameworkStatusEnum } = useTranslation();
    const { permissions } = useUserContext();
    const { exportFrameworksInBackground } = useCustomListExport();

    const [showCreateFromTemplateDialog, setShowCreateFromTemplateDialog] = useState<CreateFromTemplateViewModel>({ display: false, data: undefined });
    const [showCreateCustomFrameworkDialog, setShowCreateCustomFrameworkDialog] = useState(false);
    const [frameworkToEdit, setFrameworkToEdit] = useState<string | undefined>(undefined);
    const [frameworkForDeletion, setFrameworkForDeletion] = useState<FrameworkRowTableModel | undefined>(undefined);
    const [searchString, setSearchString] = useState("");

    const savedViewQuery = useSavedViewQuery(FilterPage.Frameworks);
    const {
        currentViewId,
        tableSessionStateKey,
        getFilterData,
        getDisplayData,
        setFilterData,
        setDisplayData,
        clearFilterData,
        hasFilterChanged,
        hasDisplayChanged,
        isSavedView,
        savedView,
        setView,
        pinnedFilterIds,
        setPinnedFilterIds,
    } = usePageView<FrameworksSavedFilter, Array<FrameworkColumn>>(FilterPage.Frameworks, savedViewQuery.data);

    const filterData = getFilterData<FrameworksSavedFilter>() ?? defaultFrameworksFilter;
    const { selectFilters, filterOperators } = filterData;
    // The backend system-default view supplies the default columns once saved views resolve; until then the page shows
    // its spinner, so an empty fallback never renders.
    const visibleColumns = getDisplayData<Array<FrameworkColumn>>() ?? [];

    // The backend only checks group company access on frameworks, so a user restricted to their own data must keep asking for their own frameworks only.
    const onlyUserSpecific = !permissions.canAccessAllData || filterData.showOnlyMine;
    const url = `/frameworks?onlyUserSpecific=${onlyUserSpecific}`;
    // Waits for the saved views, because the view decides onlyUserSpecific and would otherwise fetch the list twice.
    const { isLoading, data, refetch } = useDotLegalQuery<FrameworkOverviewModel>(url, { enabled: !savedViewQuery.isLoading });
    const { isLoading: templatesIsLoading } = useFetchFrameworkTemplates(false);
    const { data: frameworksPlanLimitData, refetch: refetchFrameworksLimitPlan } = GetFrameworkPlanLimit();

    // Arriving from a planned framework link carries the framework template in the URL. Seeded only once the saved
    // views have loaded, so the link's filter overrides the default view instead of being overwritten by it.
    useSeedFilterFromUrl(!savedViewQuery.isLoading, [frameworkTemplateFilterKey], (params) => {
        setFilterData({
            ...filterData,
            selectFilters: { [frameworkTemplateFilterId]: [params.get(frameworkTemplateFilterKey)!] },
            filterOperators: {},
            showOnlyMine: false,
        });
    });

    const isCertificationColumnAvailable = featureFlags.isEnabled(FeatureFlag.FrameworkCertification) && addonsFeatures.declarations;
    const availableColumns = allFrameworkColumns.filter((column) => column !== FrameworkColumn.CertificationRequired || isCertificationColumnAvailable);

    const columnLabels: Record<FrameworkColumn, string> = {
        [FrameworkColumn.Framework]: translateString("framework"),
        [FrameworkColumn.CertificationRequired]: translateString("requireCertification"),
        [FrameworkColumn.Owner]: translateString("frameworkOwner"),
        [FrameworkColumn.ComplianceAreas]: translateString("complianceAreas"),
        [FrameworkColumn.GroupCompanies]: translateString("groupCompanies"),
        [FrameworkColumn.Controls]: translateString("controls"),
        [FrameworkColumn.Status]: translateString("statusOfFramework"),
        [FrameworkColumn.Progress]: translateString("progress"),
    };

    const tableFields: Array<TableFields> = availableColumns.map((column) => ({
        id: column,
        name: columnLabels[column],
        grouping: FrameworkFieldGrouping.Framework,
    }));

    // A saved view persists raw column ids, so one written before a column was removed, or while the customer still
    // had certification, would still list it. Drop anything not offered rather than rendering a header with no cell.
    const renderedColumns = visibleColumns.filter((column) => availableColumns.includes(column));

    const tableHeaders: Array<ITableHeader<FrameworkRowTableModel>> = renderedColumns.map((column) => ({
        property: sortPropertyByColumn[column],
        text: columnLabels[column],
        align: "left",
        showOnMobile: true,
        tooltip: column === FrameworkColumn.Progress ? translateString("frameworkProgressInfo") : undefined,
    }));

    const allGroupCompanyIds = (data?.selectableGroupCompanies ?? []).map((x) => x.id);

    const filteredFrameworks: Array<FrameworkRowViewModel> =
        applyClientFilters(
            data?.rows,
            [
                { id: frameworkFilterId, candidate: (row) => row.framework },
                { id: "owner", candidate: (row) => row.owner.id },
                { id: "complianceAreas", candidate: (row) => row.complianceAreas.map((x) => x.id) },
                // A framework without group companies covers the entire group, so it counts as having all of them.
                { id: "groupCompanies", candidate: (row) => (row.groupCompanies.length === 0 ? allGroupCompanyIds : row.groupCompanies.map((x) => x.id)) },
                { id: "status", candidate: (row) => row.status.toString() },
                { id: frameworkTemplateFilterId, candidate: (row) => row.standardFrameworkTemplateId?.toString() ?? customFrameworkTemplateId },
            ],
            selectFilters,
            {},
            filterOperators
        ) ?? [];

    const frameworkRows = formatDataToTable(filteredFrameworks);

    // Shared by the table and the export so both order rows identically.
    const sortOrderBy = (filterData.sortByColumn as keyof FrameworkRowTableModel | undefined) ?? sortPropertyByColumn[FrameworkColumn.Framework];
    const sortOrder = filterData.sortDirection ?? "asc";

    function formatDataToTable(rows: Array<FrameworkRowViewModel>): Array<FrameworkRowTableModel> {
        return rows.map((framework) => {
            return {
                ...framework,
                ownerAsString: framework.owner.name,
                complianceAreaAsString: framework.complianceAreas.map((x) => x.name).join(", "),
                groupCompaniesAsString: framework.groupCompanies.map((x) => x.name).join(", "),
                statusAsString: translateFrameworkStatusEnum(framework.status.toString()),
            };
        });
    }

    function getFilterOptions(): Array<FilterOption> {
        const filterOptions: Array<FilterOption> = [
            {
                id: frameworkFilterId,
                label: translateString("framework"),
                type: FilterOptionType.SelectField,
                value: selectFilters[frameworkFilterId] ?? [],
                fixedData: getUniqueFilterOptions(data?.rows, (row) => row.framework).sort(sortByName),
            },
            {
                id: "owner",
                label: translateString("frameworkOwner"),
                type: FilterOptionType.SelectField,
                value: selectFilters.owner ?? [],
                fixedData: data?.selectableOwners ?? [],
            },
            {
                id: "complianceAreas",
                label: translateString("complianceAreas"),
                type: FilterOptionType.SelectField,
                value: selectFilters.complianceAreas ?? [],
                fixedData: data?.selectableComplianceAreas ?? [],
            },
            {
                id: "groupCompanies",
                label: translateString("groupCompanies"),
                type: FilterOptionType.SelectField,
                value: selectFilters.groupCompanies ?? [],
                fixedData: data?.selectableGroupCompanies ?? [],
            },
            {
                id: "status",
                label: translateString("statusOfFramework"),
                type: FilterOptionType.SelectField,
                value: selectFilters.status ?? [],
                fixedData: (data?.selectableStatus ?? []).map((x) => ({ id: x.toString(), name: translateFrameworkStatusEnum(x.toString()) })),
            },
            {
                id: frameworkTemplateFilterId,
                label: translateString("frameworkTemplate"),
                type: FilterOptionType.SelectField,
                value: selectFilters[frameworkTemplateFilterId] ?? [],
                fixedData: data?.selectableFrameworkTemplates ?? [],
            },
        ];

        if (permissions.canAccessAllData) {
            filterOptions.unshift({
                id: showOnlyMineFilterId,
                label: translateString("show"),
                type: FilterOptionType.Boolean,
                value: filterData.showOnlyMine,
                fixedData: [
                    { id: "0", name: translateString("all") },
                    { id: "1", name: translateString("my") },
                ],
            });
        }

        return withOperators(filterOptions, filterOperators);
    }

    // Cleared filters are dropped (no empty arrays, no orphaned operators) so the persisted shape deep-equals a saved
    // view's JSON round-trip, otherwise change detection would flag phantom differences.
    function onFilterChange(updatedOptions: Array<FilterOption>) {
        const nextSelect: Record<string, Array<string>> = {};
        const nextOperators: Record<string, FilterOperatorUi> = {};
        let nextShowOnlyMine = false;

        updatedOptions.forEach((option) => {
            if (option.type === FilterOptionType.SelectField) {
                const values = GetListData(option.value);
                if (values.length > 0) {
                    nextSelect[option.id] = values;
                    if (option.filterOperator !== undefined) {
                        nextOperators[option.id] = option.filterOperator;
                    }
                }
            }
            if (option.id === showOnlyMineFilterId) {
                nextShowOnlyMine = option.value === true;
            }
        });

        setFilterData({ ...filterData, selectFilters: nextSelect, filterOperators: nextOperators, showOnlyMine: nextShowOnlyMine });
    }

    // DotLegalTable also emits onChange on page changes, carrying the unchanged sort, so only a real sort change is
    // written to the view.
    function onTableStateChange(state: DotLegalTableState<FrameworkRowTableModel>) {
        if (!state.property) {
            return;
        }

        const orderBy = String(state.property);
        if (orderBy !== sortOrderBy || state.order !== sortOrder) {
            setFilterData({ ...filterData, sortByColumn: orderBy, sortDirection: state.order });
        }
    }

    function onColumnsSave(columns: Array<string>) {
        setDisplayData(columns.filter(isFrameworkColumn));
    }

    function applyView(viewId: string | undefined, filter: FrameworksSavedFilter, display: Array<FrameworkColumn>) {
        const updatedView: SessionStorageSavedView = {
            page: FilterPage.Frameworks,
            viewId,
            filterData: filter,
            displayData: display,
        };
        setView(updatedView);
    }

    function onViewSaved(viewId: string) {
        applyView(viewId, filterData, visibleColumns);
    }

    function onViewDeleted(deletedViewId: string) {
        if (deletedViewId !== currentViewId) {
            return;
        }

        const fallbackView = savedViewQuery.data?.find((x: SavedView) => x.isCustomerDefault || x.isSystemDefault);
        applyView(fallbackView?.id === deletedViewId ? undefined : fallbackView?.id, filterData, visibleColumns);
    }

    function onChangeView(view: SavedView) {
        if (view.id !== currentViewId) {
            applyView(view.id, view.filterData, view.displayData);
        }
    }

    // Filtering/sort/search are client-side here, so the export mirrors the table exactly: it sends the on-screen rows
    // (filter bar + search box + current sort) in order and the backend re-fetches and formats the values.
    async function onDownloadExcelReportClick() {
        const headers: Array<FrameworkExportHeader> = renderedColumns.map((column) => ({ header: columnLabels[column], column }));

        const rows = getDisplayedRows(frameworkRows, searchString, sortOrderBy, sortOrder).map((row) => row.id);

        // The filename is uncapped, so only it also carries the active view, which distinguishes two exports of the
        // same page. The report name doubles as the Excel sheet tab, whose naming rules the generator enforces.
        const reportName = translateString("frameworks");
        const fileNameBase = `${reportName} - ${savedView!.name}`;

        await exportFrameworksInBackground(reportName, headers, rows, fileNameBase);

        snackbar.show(translateString("frameworkDataExportInProgress"), "success", true);
    }

    function refetchFrameworks() {
        queryClient.refetchQueries(getFrameworkTemplateUrl(false));
        refetch();
        refetchFrameworksLimitPlan();
    }

    return {
        frameworks: frameworkRows,
        hasFrameworks: isLoading || (data !== undefined && data!.rows.length > 0),
        isLoading: isLoading || templatesIsLoading || savedViewQuery.isLoading,
        getFilterOptions,
        onFilterChange,
        clearFilterData,
        pinnedFilterIds,
        setPinnedFilterIds,
        searchString,
        setSearchString,
        tableFields,
        visibleColumns,
        renderedColumns,
        tableHeaders,
        onColumnsSave,
        savedView,
        currentViewId,
        tableSessionStateKey,
        isSavedView,
        hasFilterChanged,
        hasDisplayChanged,
        filterData,
        onViewSaved,
        onViewDeleted,
        onChangeView,
        sortOrderBy,
        sortOrder,
        onTableStateChange,
        onDownloadExcelReportClick,
        showCreateFromTemplateDialog,
        setShowCreateFromTemplateDialog,
        frameworkForDeletion,
        setFrameworkForDeletion,
        refetchFrameworks,
        showCreateCustomFrameworkDialog,
        setShowCreateCustomFrameworkDialog,
        frameworkToEdit,
        setFrameworkToEdit,
        frameworksPlanLimitData,
    };
}
